import { flow, Instance, types } from 'mobx-state-tree';
import { Catalogue } from '../../http-requests';
import { IParams } from '../../http-requests/types';
import { RequestStateModel } from './general-models';
import { ICatalogueMaterialBalance, ICatalogueService } from '../../types';

const LinkServiceModel = types.model({
  url: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  description: types.optional(types.string, ''),
  id: types.optional(types.string, ''),
  provider: types.optional(types.string, ''),
});

export interface ILinkServiceModel extends Instance<typeof LinkServiceModel> {}

const RequestStatuses = types.model({
  getLinksCatalogue: types.optional(RequestStateModel, {}),
  createLinkService: types.optional(RequestStateModel, {}),
  editLinkService: types.optional(RequestStateModel, {}),
  getWareHouses: types.optional(RequestStateModel, {}),
});

export const CatalogueStore = types
  .model({
    linksCatalogue: types.optional(types.array(LinkServiceModel), []),
    requestStatuses: types.optional(RequestStatuses, {}),
    materials: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
    services: types.optional(
      types.array(types.frozen<ICatalogueService>()),
      []
    ),
    onLoadServices: types.optional(types.boolean, false),
    warehouses: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
    currentWareHouseId: types.optional(types.maybeNull(types.string), null),
    currentServicesGroupId: types.optional(types.maybeNull(types.string), null),
    materialBalance: types.optional(
      types.array(types.frozen<ICatalogueMaterialBalance>()),
      []
    ),
    onLoadMaterialBalance: types.optional(types.boolean, false),
    units: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
  })
  .views((self) => ({
    getMaterialByTitle(str: string): ICatalogueMaterialBalance[] {
      const result: ICatalogueMaterialBalance[] = [];

      self.materialBalance.forEach((item) => {
        const index = item.title.toLowerCase().indexOf(str.toLowerCase());

        if (index !== -1) {
          result.push(item);
        }
      });

      return result;
    },
    getServiceByTitle(str: string): ICatalogueService[] {
      const result: ICatalogueService[] = [];

      self.services.forEach((item) => {
        const index = item.title.toLowerCase().indexOf(str.toLowerCase());

        if (index !== -1) {
          result.push(item);
        }
      });

      return result;
    },
  }))
  .actions((self) => ({
    clearCatalogue() {
      self.materials = [] as any;
      self.services = [] as any;
      self.materialBalance = [] as any;
      self.currentWareHouseId = null;
      self.currentServicesGroupId = null;
    },
    setCurrentWarehouseId(id: string | null) {
      self.currentWareHouseId = id;
    },
    setCurrentServicesGroupId(id: string | null) {
      self.currentServicesGroupId = id;
    },
    getMaterials: flow(function* (params: IParams) {
      try {
        const response = yield Catalogue.GetMaterials(params);
        self.materials = response.results;

        return response.results;
      } catch (error) {
        console.error(error);
      }
    }),
    getServices: flow(function* (params: IParams) {
      try {
        self.onLoadServices = true;
        const response = yield Catalogue.GetServices(params);
        self.services = response.results;

        return response.results;
      } catch (error) {
        console.error(error);
      } finally {
        self.onLoadServices = false;
      }
    }),
    getWareHouses: flow(function* (all?: boolean) {
      try {
        self.requestStatuses.getWareHouses.loadingStates();
        const response = yield Catalogue.GetWareHouses();

        if (all || response.results.length === 1) {
          self.warehouses = response.results;
        } else {
          /* не сохраняем главный склад */
          self.warehouses = response.results.filter(
            (item: { [key: string]: any }) =>
              !item._type.includes('MainWarehouse')
          );
        }

        self.requestStatuses.getWareHouses.successStates();

        return response.results;
      } catch (error) {
        self.requestStatuses.getWareHouses.errorStates();
        console.error(error);

        return Promise.reject(error);
      } finally {
        self.requestStatuses.getWareHouses.finallyStates();
      }
    }),
    getMaterialBalance: flow(function* (params: IParams) {
      try {
        self.onLoadMaterialBalance = true;
        const response = yield Catalogue.GetMaterialBalance(params);
        self.materialBalance = response.results;

        return response.results;
      } catch (error) {
        console.error(error);
      } finally {
        self.onLoadMaterialBalance = false;
      }
    }),
    //TODO: однажды сделать LinksCatalogue Отдельным стором (когда каталоги будут использоваться больше и стор разрастётся)
    getLinksCatalogue: flow(function* () {
      try {
        self.requestStatuses.getLinksCatalogue.loadingStates();

        const response = yield Catalogue.GetLinksCatalogueServices();
        self.linksCatalogue = response.results;
        self.requestStatuses.getLinksCatalogue.successStates();

        return self.linksCatalogue;
      } catch (error) {
        self.requestStatuses.getLinksCatalogue.errorStates();
        console.error(error);
      } finally {
        self.requestStatuses.getLinksCatalogue.finallyStates();
      }
    }),
    createLinkService: flow(function* (params: IParams) {
      try {
        self.requestStatuses.createLinkService.loadingStates();

        const response = yield Catalogue.CreateLinksCatalogueService(params);
        self.requestStatuses.createLinkService.successStates();

        return response;
      } catch (error) {
        self.requestStatuses.createLinkService.errorStates();
        console.error(error);
      } finally {
        self.requestStatuses.createLinkService.finallyStates();
      }
    }),
    editLinkService: flow(function* (id: string, params: IParams) {
      try {
        self.requestStatuses.editLinkService.loadingStates();
        const response = yield Catalogue.UpdateLinksCatalogueService(
          id,
          params
        );
        self.requestStatuses.editLinkService.successStates();

        return response;
      } catch (error) {
        self.requestStatuses.editLinkService.errorStates();
        console.error(error);
      } finally {
        self.requestStatuses.editLinkService.finallyStates();
      }
    }),
    deleteLinkService: flow(function* (id: string) {
      try {
        const response = yield Catalogue.DeleteLinksCatalogueService(id);

        return response;
      } catch (error) {
        console.error(error);
      }
    }),
  }));

export interface ICatalogueStore extends Instance<typeof CatalogueStore> {}
